import { Link, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

const API_BASE_URL = "https://bluewaterdubai.websiteseoonline.com";
const IMAGE_DELIVERY_BASE = "https://imagedelivery.net/WefzwPpyYAgz4nGrybaw0Q";
const FALLBACK_IMAGE = "/assets/img/popular/popular-1-1.jpg";

const CATEGORY_LABELS = {
  residential: "Residential",
  commercial: "Commercial",
};

function buildApiUrl(pathname) {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || API_BASE_URL).replace(/\/$/, "");
  return `${baseUrl}${pathname}`;
}

function normalizeCategory(category, searchParams) {
  const routeCategory = CATEGORY_LABELS[String(category || "").toLowerCase()];
  const queryCategory = searchParams.get("typ") || searchParams.get("property_category") || "";

  return routeCategory || queryCategory;
}

function normalizeImagePath(image) {
  if (!image) {
    return FALLBACK_IMAGE;
  }

  if (/^https?:\/\//i.test(image) || image.startsWith("/")) {
    return image;
  }

  if (image.startsWith("assets/")) {
    return `/${image}`;
  }

  return `${IMAGE_DELIVERY_BASE}/${image.replace(/^\/+/, "")}`;
}

function formatPrice(price) {
  const numericPrice = Number(String(price || "").replace(/[^\d.]/g, ""));

  if (!numericPrice) {
    return "Price on request";
  }

  return `AED ${new Intl.NumberFormat("en-AE", { maximumFractionDigits: 0 }).format(numericPrice)}`;
}

function formatLocation(property) {
  return [property.community, property.city].filter(Boolean).join(" - ") || "Dubai - UAE";
}

function offeringTypeBadge(property) {
  const rawType = String(property.offering_type || property.offeringType || "").trim();
  const normalizedType = rawType.toLowerCase();

  if (!rawType) {
    return null;
  }

  if (normalizedType.includes("rent") || ["rr", "cr", "r"].includes(normalizedType)) {
    return { label: "Rent", tone: "rent" };
  }

  if (normalizedType.includes("sale") || normalizedType.includes("buy") || ["rs", "cs", "s"].includes(normalizedType)) {
    return { label: "Sale", tone: "sale" };
  }

  return { label: rawType, tone: "default" };
}

function propertyUrl(property) {
  const referenceNumber = property.reference_number || property.id;
  return referenceNumber ? `/property-details/${encodeURIComponent(referenceNumber)}` : "/property-details";
}

function absolutePropertyUrl(property) {
  const detailUrl = propertyUrl(property);

  if (/^https?:\/\//i.test(detailUrl) || typeof window === "undefined") {
    return detailUrl;
  }

  return `${window.location.origin}${detailUrl}`;
}

function whatsappMessage(property) {
  const propertyTitle = property.title || "this property";
  const referenceNumber = property.reference_number || property.id;
  const messageParts = [
    `Hello Blue Water Real Estate, I am interested in ${propertyTitle}.`,
    referenceNumber ? `Reference: ${referenceNumber}.` : "",
    `Property details: ${absolutePropertyUrl(property)}`,
    "Please share more information.",
  ];

  return messageParts.filter(Boolean).join(" ");
}

function whatsappUrl(property) {
  const rawPhone = property.agent_whatsapp || property.agent_phone || "";
  const phone = String(rawPhone).replace(/[^\d+]/g, "");
  const message = encodeURIComponent(whatsappMessage(property));

  if (!phone) {
    return `https://api.whatsapp.com/send?phone=971507455838&text=${message}`;
  }

  return `https://api.whatsapp.com/send?phone=${encodeURIComponent(phone)}&text=${message}`;
}

function phoneUrl(property) {
  const phone = String(property.agent_phone || "").replace(/[^\d+]/g, "");
  return phone ? `tel:${phone}` : "tel:+97144514747";
}

function propertyImages(property) {
  if (!Array.isArray(property.photos) || !property.photos.length) {
    return [FALLBACK_IMAGE];
  }

  return property.photos.map((photo) => normalizeImagePath(photo.image)).filter(Boolean);
}

function openImageGallery(event, images) {
  event.preventDefault();

  const galleryItems = images.filter(Boolean).map((src) => ({ src }));
  const $ = window.jQuery || window.$;

  if (galleryItems.length && $?.magnificPopup?.open) {
    $.magnificPopup.open(
      {
        items: galleryItems,
        gallery: {
          enabled: true,
        },
        type: "image",
        mainClass: "mfp-zoom-in",
        removalDelay: 260,
      },
      0
    );
    return;
  }

  if (galleryItems[0]) {
    window.location.href = galleryItems[0].src;
  }
}

function PropertyCard({ property }) {
  const images = propertyImages(property);
  const primaryImage = images[0] || FALLBACK_IMAGE;
  const badge = offeringTypeBadge(property);

  return (
    <div className="col-xxl-3 col-xl-3 col-lg-6 col-md-6">
      <div className="popular-list-1 property-listing-card">
        <div className="thumb-wrapper">
          <Link to={propertyUrl(property)} className="property-listing-image">
            <img src={primaryImage} alt={property.title || "Property"} />
          </Link>
          {badge ? <span className={`property-offering-badge ${badge.tone}`}>{badge.label}</span> : null}
          <div className="actions-style-2-wrapper">
            <div className="actions style-2">
              <Link to={propertyUrl(property)} className="icon-btn">
                <i className="fa-solid fa-link" />
              </Link>
              <button type="button" className="icon-btn property-gallery-button" onClick={(event) => openImageGallery(event, images)}>
                <i className="fa-solid fa-camera" />
              </button>
            </div>
          </div>
        </div>
        <div className="property-content">
          <div className="media-body">
            <h3 className="box-title">
              <Link to={propertyUrl(property)}>{property.title || "Property"}</Link>
            </h3>
            <div className="box-text">
              <div className="icon">
                <img src="/assets/img/icon/2.webp" alt="location" />
              </div>
              {formatLocation(property)}
            </div>
          </div>
          <ul className="property-featured">
            <li>
              <div className="icon">
                <img src="/assets/img/icon/bed.svg" alt="bed" />
              </div>
              Bed {property.bedrooms || "-"}
            </li>
            <li>
              <div className="icon">
                <img src="/assets/img/icon/bath.svg" alt="bath" />
              </div>
              Bath {property.No_of_Bathroom || "-"}
            </li>
            <li>
              <div className="icon">
                <img src="/assets/img/icon/sqft.svg" alt="area" />
              </div>
              {property.size || "-"} sqft
            </li>
          </ul>
          <div className="property-bottom">
            <h6 className="box-title">{formatPrice(property.price)}</h6>
            <a className="th-btn sm style3 pill" href={phoneUrl(property)} aria-label="Call agent">
              <i className="fa-solid fa-phone" />
            </a>
            <a className="th-btn sm style3 pill property-whatsapp-button" href={whatsappUrl(property)} target="_blank" rel="noreferrer" aria-label="WhatsApp agent">
              <img src="/assets/img/icon/3.webp" width="38" alt="WhatsApp" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function PropertyListing() {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const activeType = useMemo(() => normalizeCategory(category, searchParams), [category, searchParams]);
  const [properties, setProperties] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    const controller = new AbortController();

    async function loadProperties() {
      setStatus("loading");

      try {
        const response = await fetch(buildApiUrl("/api/propertylistingbytype.ashx"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ typ: activeType }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error("Unable to load property listings");
        }

        const payload = await response.json();
        setProperties(payload.result === 1 && Array.isArray(payload.data) ? payload.data : []);
        setStatus("ready");
      } catch (err) {
        if (err.name !== "AbortError") {
          setProperties([]);
          setStatus("error");
        }
      }
    }

    loadProperties();

    return () => controller.abort();
  }, [activeType]);

  return (
    <>
      <div className="breadcumb-wrapper background-image" style={{ backgroundImage: "url(/assets/img/bg/breadcrumb-bg.jpg)" }}>
        <div className="container">
          <div className="breadcumb-content">
            <h1 className="breadcumb-title">{activeType ? `${activeType} Properties` : "Property Listing"}</h1>
            <ul className="breadcumb-menu">
              <li>
                <Link to="/">Home</Link>
              </li>
              <li>{activeType ? `${activeType} Properties` : "Property Listing"}</li>
            </ul>
          </div>
        </div>
      </div>

      <section className="popular-sec-1 space property-listing-page">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-7">
              <div className="title-area text-center">
                <h2 className="sec-title">{activeType ? `${activeType} Properties` : "All Properties"}</h2>
              </div>
            </div>
          </div>

          {status === "loading" && <div className="text-center">Loading properties...</div>}
          {status === "error" && <div className="text-center">Unable to load properties.</div>}
          {status === "ready" && properties.length === 0 && <div className="text-center">No properties found.</div>}
          {status === "ready" && properties.length > 0 && (
            <div className="row gy-30 justify-content-center">
              {properties.map((property) => (
                <PropertyCard property={property} key={property.id || property.reference_number || property.title} />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export default PropertyListing;
